import { Card, Col, DatePicker, Divider, Form, Input, Row, Select } from 'antd'
import { FormInstance } from 'antd/lib'

import { useClassifierServiceGetCountryClassifierElementListEndpoint } from 'src/api/queries'
import {
  Gender,
  GetPersonByIdResponse,
  PersonIdentityStatus
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { CUATMTreeSelect, Text } from 'src/components'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { RESIDENCE_ENVIRONMENT_IDS } from '../constants'

import { useStyles } from './styles'

type Props = {
  form: FormInstance
  person?: GetPersonByIdResponse
}

export const EpidemiologyPatientInformationCard = ({
  form: _form,
  person
}: Props) => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const { data: countries } =
    useClassifierServiceGetCountryClassifierElementListEndpoint()

  const countryOptions = countries?.data?.map((c) => ({
    value: c.isoCode,
    label: c[currentLang.value]
  }))

  const identityStatusOptions = Object.values(PersonIdentityStatus).map(
    (status) => ({
      value: status,
      label: t(`epidemiology.patient_status.${status}`)
    })
  )

  const genderOptions = Object.values(Gender).map((g) => ({
    value: g,
    label: t(`form.${g}`)
  }))

  const residenceEnvironmentOptions = [
    {
      value: RESIDENCE_ENVIRONMENT_IDS.urban,
      label: t('hiv_anamnesis.patient.residence.urban')
    },
    {
      value: RESIDENCE_ENVIRONMENT_IDS.rural,
      label: t('hiv_anamnesis.patient.residence.rural')
    }
  ]

  // Only Urban/Rural: the value stays the stored { id, name } classifier shape
  // (the backend sets both translations). Any other stored id (older forms)
  // is shown empty, so it has to be picked again.
  const residenceEnvironmentProps = {
    getValueProps: (val?: { id?: string }) => ({
      value: residenceEnvironmentOptions.some((o) => o.value === val?.id)
        ? val?.id
        : undefined
    }),
    getValueFromEvent: (id?: string) =>
      id
        ? {
            id,
            name: {
              [currentLang.value]: residenceEnvironmentOptions.find(
                (o) => o.value === id
              )?.label
            }
          }
        : undefined
  }

  return (
    <Card title={t('epidemiology.patient_card_title')}>
      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'identityStatus']}
            label={t('epidemiology.patient_status.title')}
            rules={requiredRules}
          >
            <Select
              disabled={!!person}
              options={identityStatusOptions}
              placeholder={t('epidemiology.patient_status.title')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name={['form', 'patient', 'idnp']} label={t('form.idnp')}>
            <Input disabled={!!person?.idnp} placeholder={t('form.idnp')} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'birthDate']}
            label={t('form.birth_date')}
            rules={requiredRules}
          >
            <DatePicker
              disabled={!!person?.birthDate}
              className="full-width"
              format={dayMonthYearFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'lastName']}
            label={t('form.last_name')}
            rules={requiredRules}
          >
            <Input
              disabled={!!person?.lastName}
              placeholder={t('form.last_name')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'firstName']}
            label={t('form.first_name')}
            rules={requiredRules}
          >
            <Input
              disabled={!!person?.firstName}
              placeholder={t('form.first_name')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'gender']}
            label={t('form.gender')}
            rules={requiredRules}
          >
            <Select
              disabled={!!person?.gender}
              options={genderOptions}
              placeholder={t('form.gender')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'citizenship']}
            label={t('common.citizenship')}
          >
            <Select
              disabled={!!person?.citizenship}
              showSearch
              optionFilterProp="label"
              options={countryOptions}
              placeholder={t('common.citizenship')}
            />
          </Form.Item>
        </Col>

        <Divider className={styles.divider} />

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'workInstitution', 'location']}
            label={t('epidemiology.work_place')}
          >
            <Input placeholder={t('epidemiology.work_place')} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'workInstitution', 'title']}
            label={t('organizational_structure.name')}
          >
            <Input placeholder={t('organizational_structure.name')} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'workInstitution', 'address']}
            label={t('cases.institution_address')}
          >
            <Input placeholder={t('cases.institution_address')} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'workInstitution', 'function']}
            label={t('epidemiology.work_function')}
          >
            <Input placeholder={t('epidemiology.work_function')} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'workInstitution', 'lastPresentDate']}
            label={t('epidemiology.last_present_date')}
          >
            <DatePicker
              className="full-width"
              disabledDate={disabledFutureDate}
              format={dayMonthYearFormatWithDot}
              placeholder={t('epidemiology.last_present_date')}
            />
          </Form.Item>
        </Col>

        <Divider className={styles.divider} />

        <Col span={24}>
          <Text size="xl">{t('epidemiology.address')}</Text>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'currentAddress', 'country']}
            label={t('common.country')}
            rules={requiredRules}
          >
            <Select
              disabled={!!person?.currentAddress?.country}
              showSearch
              optionFilterProp="label"
              options={countryOptions}
              placeholder={t('common.country')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'currentAddress', 'street']}
            label={t('form.street')}
          >
            <Input
              disabled={!!person?.currentAddress?.street}
              placeholder={t('form.street')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'currentAddress', 'streetNumber']}
            label={t('epidemiology.street_number')}
          >
            <Input
              disabled={!!person?.currentAddress?.streetNumber}
              placeholder={t('epidemiology.street_number')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'currentAddress', 'apartmentNumber']}
            label={t('epidemiology.apartment_number')}
          >
            <Input
              disabled={!!person?.currentAddress?.apartmentNumber}
              placeholder={t('epidemiology.apartment_number')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'currentAddress', 'cuatmLocality']}
            label={t('organizational_structure.city')}
            getValueProps={(val) =>
              val?.id
                ? {
                    value: {
                      value: val.id,
                      label: val.name?.[currentLang.value] ?? ''
                    }
                  }
                : { value: undefined }
            }
            getValueFromEvent={(value) => {
              if (!value?.value) return undefined

              return {
                id: value.value,
                name: { [currentLang.value]: value.label }
              }
            }}
          >
            <CUATMTreeSelect
              labelInValue
              disabled={!!person?.currentAddress?.cuatmLocality}
              placeholder={t('organizational_structure.city')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'currentAddress', 'residenceEnvironment']}
            label={t('epidemiology.residence_environment')}
            rules={requiredRules}
            {...residenceEnvironmentProps}
          >
            <Select
              allowClear
              placeholder={t('epidemiology.residence_environment')}
              options={residenceEnvironmentOptions}
            />
          </Form.Item>
        </Col>

        <Divider className={styles.divider} />

        <Col span={24}>
          <Text size="xl">{t('epidemiology.residential_address')}</Text>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'residenceAddress', 'country']}
            label={t('common.country')}
          >
            <Select
              disabled={!!person?.residenceAddress?.country}
              showSearch
              optionFilterProp="label"
              options={countryOptions}
              placeholder={t('common.country')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'residenceAddress', 'street']}
            label={t('form.street')}
          >
            <Input
              disabled={!!person?.residenceAddress?.street}
              placeholder={t('form.street')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'residenceAddress', 'streetNumber']}
            label={t('epidemiology.street_number')}
          >
            <Input
              disabled={!!person?.residenceAddress?.streetNumber}
              placeholder={t('epidemiology.street_number')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'residenceAddress', 'apartmentNumber']}
            label={t('epidemiology.apartment_number')}
          >
            <Input
              disabled={!!person?.residenceAddress?.apartmentNumber}
              placeholder={t('epidemiology.apartment_number')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={['form', 'patient', 'residenceAddress', 'cuatmLocality']}
            label={t('organizational_structure.city')}
            getValueProps={(val) =>
              val?.id
                ? {
                    value: {
                      value: val.id,
                      label: val.name?.[currentLang.value] ?? ''
                    }
                  }
                : { value: undefined }
            }
            getValueFromEvent={(value) => {
              if (!value?.value) return undefined

              return {
                id: value.value,
                name: { [currentLang.value]: value.label }
              }
            }}
          >
            <CUATMTreeSelect
              labelInValue
              disabled={!!person?.residenceAddress?.cuatmLocality}
              placeholder={t('organizational_structure.city')}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name={[
              'form',
              'patient',
              'residenceAddress',
              'residenceEnvironment'
            ]}
            label={t('epidemiology.residence_environment')}
            rules={requiredRules}
            {...residenceEnvironmentProps}
          >
            <Select
              allowClear
              placeholder={t('epidemiology.residence_environment')}
              options={residenceEnvironmentOptions}
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  )
}
